import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import { useWalletContext } from "./wallet-context";
import { api } from "../api";

// Auth storage keys
const AUTH_TOKEN_KEY = "cc_auth_token";
const AUTH_EXPIRES_KEY = "cc_auth_expires";

interface AuthUser {
  wallet: string;
  isAdmin: boolean;
  label?: string | null;
  permissions?: string[] | null;
}

interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isLoading: boolean;
  error: string | null;
  login: () => Promise<boolean>;
  logout: () => void;
  checkAdminStatus: (wallet: string) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { connected, publicKey, signMessage, disconnect } = useWalletContext();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check for existing session on mount
  useEffect(() => {
    async function checkSession() {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const expiresStr = localStorage.getItem(AUTH_EXPIRES_KEY);

      if (!token || !expiresStr) {
        setIsLoading(false);
        return;
      }

      const expiresAt = new Date(expiresStr);
      if (expiresAt <= new Date()) {
        // Token expired
        localStorage.removeItem(AUTH_TOKEN_KEY);
        localStorage.removeItem(AUTH_EXPIRES_KEY);
        setIsLoading(false);
        return;
      }

      // Validate token with API
      try {
        const response = await api.auth.me.get();
        if (response.data && "wallet" in response.data) {
          setUser({
            wallet: response.data.wallet,
            isAdmin: response.data.isAdmin,
            label: response.data.label,
            permissions: response.data.permissions,
          });
        } else {
          // Token invalid
          localStorage.removeItem(AUTH_TOKEN_KEY);
          localStorage.removeItem(AUTH_EXPIRES_KEY);
        }
      } catch {
        // Token validation failed
        localStorage.removeItem(AUTH_TOKEN_KEY);
        localStorage.removeItem(AUTH_EXPIRES_KEY);
      }

      setIsLoading(false);
    }

    checkSession();
  }, []);

  // Clear user when wallet disconnects
  useEffect(() => {
    if (!connected && user) {
      logout();
    }
  }, [connected]);

  const logout = useCallback(() => {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(AUTH_EXPIRES_KEY);
    setUser(null);
    setError(null);
  }, []);

  const login = useCallback(async (): Promise<boolean> => {
    if (!connected || !publicKey) {
      setError("Wallet not connected");
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Step 1: Get challenge message
      const challengeResponse = await api.auth.challenge.get({
        query: { wallet: publicKey },
      });

      if (!challengeResponse.data || "error" in challengeResponse.data) {
        throw new Error(
          (challengeResponse.data as any)?.error ?? "Failed to get challenge"
        );
      }

      const { message, nonce } = challengeResponse.data;

      // Step 2: Sign the message
      const signature = await signMessage(message);

      // Step 3: Verify signature and get JWT
      const verifyResponse = await api.auth.verify.post({
        wallet: publicKey,
        signature,
        message,
        nonce,
      });

      if (!verifyResponse.data || "error" in verifyResponse.data) {
        const errorMsg =
          (verifyResponse.data as any)?.error ?? "Verification failed";
        throw new Error(errorMsg);
      }

      const { token, expiresAt, isAdmin } = verifyResponse.data;

      // Store token
      localStorage.setItem(AUTH_TOKEN_KEY, token);
      localStorage.setItem(AUTH_EXPIRES_KEY, expiresAt);

      // Fetch full user info
      const meResponse = await api.auth.me.get();
      if (meResponse.data && "wallet" in meResponse.data) {
        setUser({
          wallet: meResponse.data.wallet,
          isAdmin: meResponse.data.isAdmin,
          label: meResponse.data.label,
          permissions: meResponse.data.permissions,
        });
      } else {
        setUser({
          wallet: publicKey,
          isAdmin,
        });
      }

      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : "Login failed";
      setError(message);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [connected, publicKey, signMessage]);

  const checkAdminStatus = useCallback(
    async (wallet: string): Promise<boolean> => {
      try {
        const response = await api.auth.check({ wallet }).get();
        if (response.data && "isAdmin" in response.data) {
          return response.data.isAdmin;
        }
        return false;
      } catch {
        return false;
      }
    },
    []
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isAdmin: user?.isAdmin ?? false,
        isLoading,
        error,
        login,
        logout,
        checkAdminStatus,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
